<!--
  Pour record — the unit of work.

  Every section is a <details> whose summary states what is outstanding, so a
  half-finished pour is obvious without opening anything. <details> is used
  rather than a bespoke accordion because it is native, keyboard- and
  screen-reader-correct, and cannot trap the user the way a modal can.

  ── Division of labour ─────────────────────────────────────────────────────
  This screen owns three editable sections directly — pour details, header,
  and material/loading — plus photos and the close action.

  Five sections are components owned by the sections agent. This file is the
  ONLY place they are mounted, and it is the owner of the canonical data they
  render: they receive server rows as props and hand mutations back through
  callbacks, and this file performs the API call, merges the server's answer
  into `currentPour`, and lets the change flow back down.

    sections/Calibration.svelte   { pourId, calibration, trials, readonly, onSave }
    sections/ChuteTests.svelte    { pourId, chuteTests, shiftName, readonly,
                                    onCreate, onUpdate, onDelete }
    sections/ItpChecklist.svelte  { pourId, items, shiftName, readonly,
                                    onUpdate, onRecordStopHold }
    sections/StopHold.svelte      { pourId, events, readonly,
                                    onCreate, onUpdate, onDelete }
    sections/EndOfPour.svelte     { pourId, items, shiftName, readonly, onUpdate }

  Every callback here goes through `flushChild`, which records a failure on the
  shared unsaved bar AND rethrows, so the child keeps its own local draft. A
  save must never look like it succeeded when it did not — that is the one way
  this app could lose a record.
  ────────────────────────────────────────────────────────────────────────────
-->
<script lang="ts">
  import Calibration from './sections/Calibration.svelte';
  import ChuteTests from './sections/ChuteTests.svelte';
  import EndOfPour from './sections/EndOfPour.svelte';
  import ItpChecklist from './sections/ItpChecklist.svelte';
  import StopHold from './sections/StopHold.svelte';

  import Export from './Export.svelte';
  import PhotoField from '../lib/fields/PhotoField.svelte';

  import { ApiError, api, exportUrl, isApiError, save } from '../lib/api';
  // Photos go through the persistent queue in ../lib/photo.ts, which survives a
  // reload; PhotoField is the capture control that drives it.
  import { onPhotoAttached } from '../lib/photo';
  import { linkHandler, routePath } from '../lib/router';
  import {
    canEdit,
    currentPour,
    currentPourLoading,
    isHoldPoint,
    loadPour,
    mergeCurrentPour,
    notifyError,
    replaceItpItem,
    sectionDraft,
    session,
    toInput,
    toNumber,
    toText,
    type SectionDraft
  } from '../lib/store';
  import type {
    ChuteTest,
    CreateChuteTestRequest,
    CreateStopHoldEventRequest,
    EndOfPourItem,
    ItpItem,
    Photo,
    PrePourCalibrationPatch,
    CalibrationCheckPatch,
    CalibrationTrialPatch,
    StopHoldEvent,
    UpdateChuteTestRequest,
    UpdateEndOfPourItemRequest,
    UpdateItpItemRequest,
    UpdateStopHoldEventRequest
  } from '../lib/types';

  interface Props {
    pourId: number;
  }

  let { pourId }: Props = $props();

  /* ---------------------------------------------------------------- types */

  /** Type aliases (not interfaces) so they satisfy Record<string, unknown>. */
  type PourFields = { label: string | null; date: string | null };

  type HeaderFields = {
    pour_location: string | null;
    sci_unit_no: string | null;
    operator: string | null;
    feed_water_control_operator: string | null;
    pump_operator: string | null;
    kiewit_qc_tester: string | null;
    water_source: string | null;
    material_lot_no: string | null;
  };

  type MaterialFields = {
    product_name: string | null;
    lot_batch_no: string | null;
    supersack_numbers: string | null;
    quantity_loaded: number | null;
    packaging_condition: string | null;
    storage_condition: string | null;
    loaded_by: string | null;
  };

  const HEADER_FIELDS: { field: keyof HeaderFields; label: string }[] = [
    { field: 'pour_location', label: 'Pour location' },
    { field: 'sci_unit_no', label: 'SCI unit no' },
    { field: 'operator', label: 'Operator' },
    { field: 'feed_water_control_operator', label: 'Feed / water control operator' },
    { field: 'pump_operator', label: 'Pump operator' },
    { field: 'kiewit_qc_tester', label: 'Kiewit QC tester' },
    { field: 'water_source', label: 'Water source' },
    { field: 'material_lot_no', label: 'Material lot no' }
  ];

  const MATERIAL_FIELDS: {
    field: keyof MaterialFields;
    label: string;
    numeric?: boolean;
    unit?: string;
  }[] = [
    { field: 'product_name', label: 'Product name' },
    { field: 'lot_batch_no', label: 'Lot / batch no' },
    { field: 'supersack_numbers', label: 'Supersack numbers' },
    { field: 'quantity_loaded', label: 'Quantity loaded', numeric: true, unit: 'kg' },
    { field: 'packaging_condition', label: 'Packaging condition' },
    { field: 'storage_condition', label: 'Storage condition' },
    { field: 'loaded_by', label: 'Loaded by' }
  ];

  /* --------------------------------------------------------------- state */

  let pourValues = $state<PourFields>({ label: null, date: null });
  let headerValues = $state<HeaderFields>({
    pour_location: null,
    sci_unit_no: null,
    operator: null,
    feed_water_control_operator: null,
    pump_operator: null,
    kiewit_qc_tester: null,
    water_source: null,
    material_lot_no: null
  });
  let materialValues = $state<MaterialFields>({
    product_name: null,
    lot_batch_no: null,
    supersack_numbers: null,
    quantity_loaded: null,
    packaging_condition: null,
    storage_condition: null,
    loaded_by: null
  });

  let pourDirty = $state(false);
  let headerDirty = $state(false);
  let materialDirty = $state(false);
  let restoredDraft = $state(false);

  let pourDraft: SectionDraft<PourFields> | undefined;
  let headerDraft: SectionDraft<HeaderFields> | undefined;
  let materialDraft: SectionDraft<MaterialFields> | undefined;

  let closing = $state(false);
  let confirmingClose = $state(false);
  let blockingItems = $state<ItpItem[]>([]);
  let stopHoldDetails = $state<HTMLDetailsElement | undefined>(undefined);

  /** Plain (non-reactive) guard so the init effect cannot loop on itself. */
  let initialisedFor = -1;

  /* ---------------------------------------------------------------- load */

  $effect(() => {
    const id = pourId;
    if (initialisedFor === id) return;
    initialisedFor = id;
    void init(id);
  });

  async function init(id: number) {
    const record = await loadPour(id);
    if (!record) return;

    pourDraft = sectionDraft<PourFields>({
      key: `pour:${id}:pour`,
      label: 'Pour details',
      base: { label: record.pour.label, date: record.pour.date },
      save: async (changed) => {
        const response = await api.updatePour(id, {
          pour: {
            ...(changed.label !== undefined ? { label: changed.label ?? '' } : {}),
            ...(changed.date !== undefined ? { date: changed.date ?? '' } : {})
          }
        });
        mergeCurrentPour(response.pour);
        pourDraft?.rebase({ label: response.pour.pour.label, date: response.pour.pour.date });
      }
    });

    headerDraft = sectionDraft<HeaderFields>({
      key: `pour:${id}:header`,
      label: 'Pour header',
      base: pickHeader(record.header),
      save: async (changed) => {
        const response = await api.updatePour(id, { header: changed });
        mergeCurrentPour(response.pour);
        headerDraft?.rebase(pickHeader(response.pour.header));
      }
    });

    materialDraft = sectionDraft<MaterialFields>({
      key: `pour:${id}:material`,
      label: 'Material and loading',
      base: pickMaterial(record.material_loading),
      save: async (changed) => {
        const response = await api.updatePour(id, { material_loading: changed });
        mergeCurrentPour(response.pour);
        materialDraft?.rebase(pickMaterial(response.pour.material_loading));
      }
    });

    pourDraft.subscribe((next) => (pourValues = { ...next }));
    pourDraft.dirty.subscribe((next) => (pourDirty = next));
    headerDraft.subscribe((next) => (headerValues = { ...next }));
    headerDraft.dirty.subscribe((next) => (headerDirty = next));
    materialDraft.subscribe((next) => (materialValues = { ...next }));
    materialDraft.dirty.subscribe((next) => (materialDirty = next));

    restoredDraft = pourDraft.restored || headerDraft.restored || materialDraft.restored;
  }

  function pickHeader(row: { [K in keyof HeaderFields]: HeaderFields[K] }): HeaderFields {
    return {
      pour_location: row.pour_location,
      sci_unit_no: row.sci_unit_no,
      operator: row.operator,
      feed_water_control_operator: row.feed_water_control_operator,
      pump_operator: row.pump_operator,
      kiewit_qc_tester: row.kiewit_qc_tester,
      water_source: row.water_source,
      material_lot_no: row.material_lot_no
    };
  }

  function pickMaterial(row: { [K in keyof MaterialFields]: MaterialFields[K] }): MaterialFields {
    return {
      product_name: row.product_name,
      lot_batch_no: row.lot_batch_no,
      supersack_numbers: row.supersack_numbers,
      quantity_loaded: row.quantity_loaded,
      packaging_condition: row.packaging_condition,
      storage_condition: row.storage_condition,
      loaded_by: row.loaded_by
    };
  }

  function editHeader(field: keyof HeaderFields, raw: string) {
    headerDraft?.setField(field, toText(raw));
  }

  function editMaterial(field: keyof MaterialFields, raw: string, numeric = false) {
    if (numeric) materialDraft?.setField(field, toNumber(raw) as MaterialFields[typeof field]);
    else materialDraft?.setField(field, toText(raw) as MaterialFields[typeof field]);
  }

  /* ------------------------------------------------- child save plumbing */

  /**
   * Run a child section's save. On failure it records the section on the
   * shared unsaved bar (with a working retry) AND rethrows, so the child keeps
   * its own draft rather than believing the write landed.
   */
  async function flushChild<T>(key: string, label: string, run: () => Promise<T>): Promise<T> {
    const result = await save(key, label, async () => ({ value: await run() }));
    if (result === null) {
      throw new ApiError('internal_error', `${label} was not saved. It is kept on this device.`);
    }
    return result.value;
  }

  /* ------------------------------------------------------- calibration */

  async function saveCalibration(patch: {
    calibration?: PrePourCalibrationPatch;
    calibration_trials?: CalibrationTrialPatch[];
    calibration_checks?: CalibrationCheckPatch[];
  }): Promise<void> {
    await flushChild(`pour:${pourId}:calibration`, 'Pre-pour calibration', async () => {
      const response = await api.updatePour(pourId, patch);
      mergeCurrentPour(response.pour);
    });
  }

  /* -------------------------------------------------------- chute tests */

  async function createChuteTest(body: CreateChuteTestRequest): Promise<ChuteTest> {
    return flushChild(`pour:${pourId}:chute-test:new`, 'New chute test', async () => {
      const response = await api.createChuteTest(pourId, body);
      mergeCurrentPour({ chute_tests: [...chuteTests, response.chute_test] });
      return response.chute_test;
    });
  }

  async function updateChuteTest(testId: number, body: UpdateChuteTestRequest): Promise<void> {
    await flushChild(`pour:${pourId}:chute-test:${testId}`, 'Chute test', async () => {
      const response = await api.updateChuteTest(pourId, testId, body);
      mergeCurrentPour({
        chute_tests: chuteTests.map((row) => (row.id === testId ? response.chute_test : row))
      });
    });
  }

  async function deleteChuteTest(testId: number): Promise<void> {
    await flushChild(`pour:${pourId}:chute-test:${testId}`, 'Chute test', async () => {
      await api.deleteChuteTest(pourId, testId);
      mergeCurrentPour({ chute_tests: chuteTests.filter((row) => row.id !== testId) });
    });
  }

  /* ----------------------------------------------------------- ITP items */

  async function updateItpItem(
    itemId: number,
    body: UpdateItpItemRequest
  ): Promise<{ hold_point_triggered?: boolean }> {
    const item = itpItems.find((row) => row.id === itemId);
    const label = item ? `ITP item ${item.item_no}` : 'ITP item';
    return flushChild(`pour:${pourId}:itp:${itemId}`, label, async () => {
      const response = await api.updateItpItem(pourId, itemId, body);
      replaceItpItem(response.item);
      return { hold_point_triggered: response.hold_point_triggered };
    });
  }

  /** ItpChecklist offers "record a stop-hold" against a failed Hold point. */
  function jumpToStopHold() {
    if (!stopHoldDetails) return;
    stopHoldDetails.open = true;
    stopHoldDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------------------------------------------------- stop-hold events */

  async function createStopHoldEvent(body: CreateStopHoldEventRequest): Promise<StopHoldEvent> {
    return flushChild(`pour:${pourId}:stop-hold:new`, 'Stop-hold event', async () => {
      const response = await api.createStopHoldEvent(pourId, body);
      mergeCurrentPour({ stop_hold_events: [...stopHoldEvents, response.stop_hold_event] });
      return response.stop_hold_event;
    });
  }

  async function updateStopHoldEvent(
    eventId: number,
    body: UpdateStopHoldEventRequest
  ): Promise<void> {
    await flushChild(`pour:${pourId}:stop-hold:${eventId}`, 'Stop-hold event', async () => {
      const response = await api.updateStopHoldEvent(pourId, eventId, body);
      mergeCurrentPour({
        stop_hold_events: stopHoldEvents.map((row) =>
          row.id === eventId ? response.stop_hold_event : row
        )
      });
    });
  }

  async function deleteStopHoldEvent(eventId: number): Promise<void> {
    await flushChild(`pour:${pourId}:stop-hold:${eventId}`, 'Stop-hold event', async () => {
      await api.deleteStopHoldEvent(pourId, eventId);
      mergeCurrentPour({ stop_hold_events: stopHoldEvents.filter((row) => row.id !== eventId) });
    });
  }

  /* --------------------------------------------------- end-of-pour items */

  async function updateEndOfPourItem(
    itemId: number,
    body: UpdateEndOfPourItemRequest
  ): Promise<void> {
    const item = eopItems.find((row) => row.id === itemId);
    const label = item ? `End-of-pour item ${item.item_no}` : 'End-of-pour item';
    await flushChild(`pour:${pourId}:end-of-pour:${itemId}`, label, async () => {
      const response = await api.updateEndOfPourItem(pourId, itemId, body);
      mergeCurrentPour({
        end_of_pour_items: eopItems.map((row: EndOfPourItem) =>
          row.id === itemId ? response.item : row
        )
      });
    });
  }

  /* --------------------------------------------------------------- close */

  async function closePour() {
    if (!$canEdit || closing) return;
    closing = true;
    blockingItems = [];
    try {
      // Flush anything still held locally before the record is sealed.
      await pourDraft?.flush();
      await headerDraft?.flush();
      await materialDraft?.flush();

      const response = await api.closePour(pourId);
      mergeCurrentPour(response.pour);
      confirmingClose = false;
    } catch (cause) {
      if (isApiError(cause) && cause.code === 'hold_point_open') {
        blockingItems = cause.blockingItems ?? [];
      } else {
        notifyError(cause, 'Could not close the pour');
      }
    } finally {
      closing = false;
    }
  }

  /* -------------------------------------------------------------- photos */

  /**
   * The upload queue lands photos on its own schedule — possibly minutes after
   * the capture, possibly after a reload. Fold each one into the open record as
   * it arrives so the gallery matches the server without re-fetching.
   */
  $effect(() =>
    onPhotoAttached((photo: Photo) => {
      if (photo.pour_id !== pourId) return;
      const current = $currentPour?.photos ?? [];
      if (current.some((existing) => existing.id === photo.id)) return;
      mergeCurrentPour({ photos: [...current, photo] });
    })
  );

  /* ------------------------------------------------------- derived state */

  function blanks(values: Record<string, unknown>, fields: string[]): number {
    return fields.filter((field) => {
      const value = values[field];
      return value === null || value === undefined || value === '';
    }).length;
  }

  function outstandingText(count: number, noun: string): string {
    return count === 0 ? 'Complete' : `${count} ${noun} outstanding`;
  }

  const pour = $derived($currentPour);
  const itpItems = $derived(pour?.itp_items ?? []);
  const eopItems = $derived(pour?.end_of_pour_items ?? []);
  const stopHoldEvents = $derived(pour?.stop_hold_events ?? []);
  const chuteTests = $derived(pour?.chute_tests ?? []);
  const photos = $derived(pour?.photos ?? []);
  /** The rest are filed against a calibration trial or a chute-test row. */
  const generalPhotos = $derived(
    photos.filter((photo) => (photo.section ?? 'general') === 'general').length
  );
  const trials = $derived(pour?.calibration_trials ?? []);

  const itpRecorded = $derived(itpItems.filter((item) => item.status !== 'pending').length);
  const eopRecorded = $derived(eopItems.filter((item) => item.status !== 'pending').length);
  const failedHolds = $derived(
    itpItems.filter((item) => item.status === 'failed' && isHoldPoint(item.point_type))
  );
  const openStopHolds = $derived(stopHoldEvents.filter((event) => !event.restart_time).length);
  const trialsFilled = $derived(
    trials.filter((trial) => trial.reading_kg !== null && trial.counts !== null).length
  );
  const isClosed = $derived(pour?.pour.status === 'closed');
  const editable = $derived($canEdit && !isClosed);
  /**
   * Photos are the one exception to "closed means read-only": the owner wants
   * evidence addable after the pour closes, so this drops the isClosed half
   * of `editable` and keeps only the role gate. A closed pour still refuses
   * everything ELSE — this const feeds PhotoField (here, and passed down into
   * Calibration and ChuteTests) and nothing else.
   */
  const photosEditable = $derived($canEdit);
  /** For PhotoField's "added after close" mark. null while the pour is open. */
  const closedAt = $derived(pour?.pour.closed_at ?? null);
  const shiftName = $derived($session?.shiftName ?? null);

  const headerBlanks = $derived(
    blanks(
      headerValues,
      HEADER_FIELDS.map((entry) => entry.field)
    )
  );
  const materialBlanks = $derived(
    blanks(
      materialValues,
      MATERIAL_FIELDS.map((entry) => entry.field)
    )
  );
</script>

<div class="row row--between titlerow">
  <a class="btn btn--sm" href={routePath.home()} onclick={linkHandler}>← Pours</a>
  {#if pour}
    <span
      class="pill {isClosed
        ? 'pill--ok'
        : pour.pour.status === 'open'
          ? 'pill--warn'
          : 'pill--pending'}"
    >
      {isClosed ? 'Closed' : pour.pour.status === 'open' ? 'Open' : 'Draft'}
    </span>
  {/if}
</div>

{#if $currentPourLoading && !pour}
  <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading the record…</p>
{:else if !pour}
  <div class="banner banner--error" role="alert">
    <div>
      <strong>Record not available</strong>
      <div class="banner__actions">
        <button class="btn btn--sm" type="button" onclick={() => void loadPour(pourId)}>
          Retry
        </button>
      </div>
    </div>
  </div>
{:else}
  <h1>{pourValues.label ?? pour.pour.label}</h1>
  <p class="meta">{pourValues.date ?? pour.pour.date}</p>

  {#if restoredDraft}
    <div class="banner banner--warn" role="status">
      <div>
        <strong>Unsent changes restored</strong>
        Work saved on this phone had not reached the server. It is shown below — check it, then
        save.
      </div>
    </div>
  {/if}

  {#if failedHolds.length > 0}
    <div class="banner banner--error" role="alert">
      <div>
        <strong>Hold point failed — production is to stop</strong>
        Notify Kiewit QC before restart, and record a stop-hold event with the corrective action.
        <ul class="holdlist">
          {#each failedHolds as item (item.id)}
            <li>{item.item_no}. {item.title}</li>
          {/each}
        </ul>
        <div class="banner__actions">
          <button class="btn btn--sm" type="button" onclick={jumpToStopHold}>
            Record a stop / hold
          </button>
        </div>
      </div>
    </div>
  {/if}

  <div class="btnrow toprow">
    <a class="btn btn--sm" href={exportUrl.pourCsv(pourId)} download>CSV</a>
    <a class="btn btn--sm" href={exportUrl.pourPdf(pourId)} download>PDF</a>
  </div>

  <!-- ============================================================ details -->
  <details class="section" open>
    <summary>
      <span class="section__label">Pour details</span>
      <span class="pill pill--pending">{pour.pour.label}</span>
    </summary>
    <div class="section__body">
      <div class="grid2">
        <div class="field">
          <span class="label" id="lbl-label">Label</span>
          {#if editable}
            <input
              type="text"
              aria-labelledby="lbl-label"
              value={toInput(pourValues.label)}
              autocapitalize="words"
              oninput={(event) => pourDraft?.setField('label', toText(event.currentTarget.value))}
              onchange={() => pourDraft?.flush()}
            />
          {:else}
            <div class="readonly-value">{pour.pour.label}</div>
          {/if}
        </div>
        <div class="field">
          <span class="label" id="lbl-date">Date</span>
          {#if editable}
            <input
              type="date"
              aria-labelledby="lbl-date"
              value={toInput(pourValues.date)}
              oninput={(event) => pourDraft?.setField('date', toText(event.currentTarget.value))}
              onchange={() => pourDraft?.flush()}
            />
          {:else}
            <div class="readonly-value">{pour.pour.date}</div>
          {/if}
        </div>
      </div>

      {#if pour.pour.created_by}
        <p class="meta">Started by {pour.pour.created_by}</p>
      {/if}
      {#if isClosed}
        <p class="meta">
          Closed {pour.pour.closed_at ? new Date(pour.pour.closed_at).toLocaleString() : ''}
          {pour.pour.closed_by ? `by ${pour.pour.closed_by}` : ''}
        </p>
      {/if}

      {#if editable && pourDirty}
        <button
          class="btn btn--primary btn--block"
          type="button"
          onclick={() => pourDraft?.flush()}
        >
          Save changes
        </button>
      {/if}
    </div>
  </details>

  <!-- ============================================================= header -->
  <details class="section">
    <summary>
      <span class="section__label">Header</span>
      <span class="pill {headerBlanks === 0 ? 'pill--ok' : 'pill--warn'}">
        {outstandingText(headerBlanks, 'blank')}
      </span>
    </summary>
    <div class="section__body">
      {#each HEADER_FIELDS as entry (entry.field)}
        <div class="field">
          <span class="label" id={`hdr-${entry.field}`}>{entry.label}</span>
          {#if editable}
            <input
              type="text"
              aria-labelledby={`hdr-${entry.field}`}
              value={toInput(headerValues[entry.field])}
              autocapitalize="words"
              oninput={(event) => editHeader(entry.field, event.currentTarget.value)}
              onchange={() => headerDraft?.flush()}
            />
          {:else}
            <div class="readonly-value" class:is-empty={!headerValues[entry.field]}>
              {headerValues[entry.field] ?? ''}
            </div>
          {/if}
        </div>
      {/each}
      {#if editable}
        <button
          class="btn btn--primary btn--block"
          type="button"
          disabled={!headerDirty}
          onclick={() => headerDraft?.flush()}
        >
          {headerDirty ? 'Save header' : 'Saved'}
        </button>
      {/if}
    </div>
  </details>

  <!-- =================================================== material/loading -->
  <details class="section">
    <summary>
      <span class="section__label">Material and loading</span>
      <span class="pill {materialBlanks === 0 ? 'pill--ok' : 'pill--warn'}">
        {outstandingText(materialBlanks, 'blank')}
      </span>
    </summary>
    <div class="section__body">
      {#each MATERIAL_FIELDS as entry (entry.field)}
        <div class="field">
          <span class="label" id={`mat-${entry.field}`}>
            {entry.label}{#if entry.unit}&nbsp;({entry.unit}){/if}
          </span>
          {#if editable}
            <input
              type="text"
              aria-labelledby={`mat-${entry.field}`}
              inputmode={entry.numeric ? 'decimal' : undefined}
              autocapitalize={entry.numeric ? 'none' : 'words'}
              value={toInput(materialValues[entry.field])}
              oninput={(event) =>
                editMaterial(entry.field, event.currentTarget.value, entry.numeric)}
              onchange={() => materialDraft?.flush()}
            />
          {:else}
            <div class="readonly-value" class:is-empty={materialValues[entry.field] === null}>
              {toInput(materialValues[entry.field])}
            </div>
          {/if}
        </div>
      {/each}
      {#if editable}
        <button
          class="btn btn--primary btn--block"
          type="button"
          disabled={!materialDirty}
          onclick={() => materialDraft?.flush()}
        >
          {materialDirty ? 'Save material' : 'Saved'}
        </button>
      {/if}
    </div>
  </details>

  <!-- ==================================================== calibration slot -->
  <details class="section">
    <summary>
      <span class="section__label">Pre-pour calibration</span>
      <span
        class="pill {trials.length > 0 && trialsFilled === trials.length
          ? 'pill--ok'
          : 'pill--warn'}"
      >
        {trialsFilled}/{trials.length || 3} trials
      </span>
    </summary>
    <div class="section__body">
      <Calibration
        {pourId}
        calibration={pour.calibration}
        trials={pour.calibration_trials}
        checks={pour.calibration_checks}
        {photos}
        readonly={!editable}
        photosReadOnly={!photosEditable}
        {closedAt}
        onSave={saveCalibration}
      />
    </div>
  </details>

  <!-- =================================================== chute tests slot -->
  <details class="section">
    <summary>
      <span class="section__label">Chute tests</span>
      <span class="pill {chuteTests.length > 0 ? 'pill--ok' : 'pill--warn'}">
        {chuteTests.length === 0 ? 'None recorded' : `${chuteTests.length} recorded`}
      </span>
    </summary>
    <div class="section__body">
      <ChuteTests
        {pourId}
        chuteTests={pour.chute_tests}
        {shiftName}
        {photos}
        readonly={!editable}
        photosReadOnly={!photosEditable}
        {closedAt}
        onCreate={createChuteTest}
        onUpdate={updateChuteTest}
        onDelete={deleteChuteTest}
      />
    </div>
  </details>

  <!-- ====================================================== ITP 6..31 slot -->
  <details class="section" class:section--hold={failedHolds.length > 0}>
    <summary>
      <span class="section__label">ITP items 6–31</span>
      {#if failedHolds.length > 0}
        <span class="pill pill--hold">{failedHolds.length} Hold failed</span>
      {/if}
      <span class="pill {itpRecorded === itpItems.length ? 'pill--ok' : 'pill--warn'}">
        {itpRecorded}/{itpItems.length} recorded
      </span>
    </summary>
    <div class="section__body">
      <ItpChecklist
        {pourId}
        items={pour.itp_items}
        {shiftName}
        readonly={!editable}
        onUpdate={updateItpItem}
        onRecordStopHold={jumpToStopHold}
      />
    </div>
  </details>

  <!-- ==================================================== stop-hold slot -->
  <details class="section" bind:this={stopHoldDetails}>
    <summary>
      <span class="section__label">Stop / hold record</span>
      <span class="pill {openStopHolds > 0 ? 'pill--warn' : 'pill--pending'}">
        {stopHoldEvents.length === 0
          ? 'None'
          : `${stopHoldEvents.length} event${stopHoldEvents.length === 1 ? '' : 's'}${openStopHolds > 0 ? `, ${openStopHolds} open` : ''}`}
      </span>
    </summary>
    <div class="section__body">
      <StopHold
        {pourId}
        events={pour.stop_hold_events}
        readonly={!editable}
        onCreate={createStopHoldEvent}
        onUpdate={updateStopHoldEvent}
        onDelete={deleteStopHoldEvent}
      />
    </div>
  </details>

  <!-- ================================================== end-of-pour slot -->
  <details class="section">
    <summary>
      <span class="section__label">End of pour</span>
      <span class="pill {eopRecorded === eopItems.length ? 'pill--ok' : 'pill--warn'}">
        {eopRecorded}/{eopItems.length} recorded
      </span>
    </summary>
    <div class="section__body">
      <EndOfPour
        {pourId}
        items={pour.end_of_pour_items}
        {shiftName}
        readonly={!editable}
        onUpdate={updateEndOfPourItem}
      />
    </div>
  </details>

  <!-- ============================================================= photos -->
  <details class="section">
    <summary>
      <span class="section__label">Photos</span>
      <span class="pill pill--pending">
        {photos.length}{generalPhotos === photos.length ? '' : ` (${generalPhotos} general)`}
      </span>
    </summary>
    <div class="section__body">
      <PhotoField
        {pourId}
        section="general"
        {photos}
        label="Pour photos"
        actionLabel="📷 Add a photo"
        readOnly={!photosEditable}
        {closedAt}
        hint="A photo taken here belongs to the pour as a whole. A batch computer photo attaches to its calibration trial, and a chute-test photo to its row — take those in those sections."
      />
    </div>
  </details>

  <!-- ============================================================= export -->
  <Export {pourId} pour={$currentPour} />

  <!-- ============================================================== close -->
  {#if $canEdit && !isClosed}
    <div class="card">
      <div class="card__body">
        {#if blockingItems.length > 0}
          <div class="banner banner--error" role="alert">
            <div>
              <strong>Cannot close — a Hold point is still failed</strong>
              Resolve it, or record a stop-hold event that closes it out.
              <ul class="holdlist">
                {#each blockingItems as item (item.id)}
                  <li>{item.item_no}. {item.title}</li>
                {/each}
              </ul>
            </div>
          </div>
        {/if}

        {#if confirmingClose}
          <p><strong>Close this pour?</strong> Closed records stay readable and exportable.</p>
          <div class="btnrow">
            <button
              class="btn btn--primary btn--big"
              type="button"
              disabled={closing}
              onclick={closePour}
            >
              {#if closing}<span class="spinner" aria-hidden="true"></span> Closing…{:else}Yes,
                close it{/if}
            </button>
            <button class="btn" type="button" onclick={() => (confirmingClose = false)}>
              Not yet
            </button>
          </div>
        {:else}
          <button
            class="btn btn--block btn--big"
            type="button"
            onclick={() => (confirmingClose = true)}
          >
            Close pour
          </button>
        {/if}
      </div>
    </div>
  {/if}
{/if}

<style>
  .titlerow {
    margin-top: 0.25rem;
  }

  .toprow {
    margin: 0.75rem 0;
  }

  .holdlist {
    margin: 0.4rem 0 0;
    padding-left: 1.1rem;
  }
</style>
